import * as React from 'react';
import {useQueries} from '@tanstack/react-query';
import {useDispatch} from 'react-redux';
import {baseRequest} from 'src/utils/redux-api-v2';
import type {CatalogWidget, CatalogGroup} from 'src/types/byod';
import {BYOD_API_BASE as BASE} from 'src/components/analytics-genesis/byod/api';
import {byodQueryDefaults} from 'src/components/analytics-genesis/byod/api/error-handling';
import {useApiQuery} from 'src/hooks/useApiQuery';


type WidgetListItem = {
  id: string;
  widget_key: string;
  widget_type: 'kpi' | 'chart' | 'table';
  name: string;
  description?: string;
  product_area: string | null;
};

type WidgetListResponse = {
  widgets: WidgetListItem[];
  pagination: {offset: number; page_size: number; total_count: number};
};

type CatalogResult = {groups: CatalogGroup[]};

const PAGE_SIZE = 20;

export function useWidgetCatalog(): {
  data: CatalogResult | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const dispatch = useDispatch();

  // 1. Fetch first page to get total_count
  const {
    data: firstPageData,
    isLoading: isFirstPageLoading,
    isError: isFirstPageError,
    refetch: refetchFirstPage,
  } = useApiQuery<WidgetListResponse>(
    {
      pathname: `${BASE}/widgets`,
      query: {limit: String(PAGE_SIZE), offset: '0'},
    },
    {
      queryOptions: {
        ...byodQueryDefaults,
        queryKey: ['byod-widgets', 0, PAGE_SIZE],
        staleTime: 30 * 1000,
        gcTime: 60 * 1000,
        refetchInterval: 60 * 1000,
      },
    },
  );

  // 2. Calculate remaining pages from total_count
  const totalCount = firstPageData?.pagination?.total_count ?? 0;
  const remainingOffsets = React.useMemo(() => {
    if (totalCount <= PAGE_SIZE) {
      return [];
    }
    const offsets: number[] = [];
    for (let offset = PAGE_SIZE; offset < totalCount; offset += PAGE_SIZE) {
      offsets.push(offset);
    }
    return offsets;
  }, [totalCount]);

  // 3. Fetch remaining pages in parallel
  const remainingQueries = useQueries({
    queries: remainingOffsets.map((offset) => ({
      ...byodQueryDefaults,
      queryKey: ['byod-widgets', offset, PAGE_SIZE],
      queryFn: async ({signal}: {signal?: AbortSignal}) => {
        const response = await dispatch(
          baseRequest(`${BASE}/widgets`, {
            method: 'GET' as const,
            query: {limit: String(PAGE_SIZE), offset: String(offset)},
            signal,
            options: {handleAuth: true},
          }),
        );
        return response as WidgetListResponse;
      },
      enabled: totalCount > PAGE_SIZE,
      staleTime: 30 * 1000,
      gcTime: 60 * 1000,
      refetchInterval: 60 * 1000,
    })),
  });

  const isRemainingLoading = remainingQueries.some((q) => q.isLoading);
  const isRemainingError = remainingQueries.some((q) => q.isError);

  const isLoading =
    isFirstPageLoading || (totalCount > PAGE_SIZE && isRemainingLoading);
  const isError = isFirstPageError || isRemainingError;

  // Collect remaining page data into a stable reference — only recomputes
  // when any page's data actually updates (dataUpdatedAt changes).
  const remainingDataTimestamps = remainingQueries
    .map((q) => q.dataUpdatedAt)
    .join(',');
  const remainingWidgets = React.useMemo(
    () => remainingQueries.flatMap((q) => q.data?.widgets ?? []),

    [remainingDataTimestamps],
  );

  // 4. Merge all pages and group by product_area
  const data = React.useMemo((): CatalogResult | null => {
    if (!firstPageData) {
      return null;
    }

    const allWidgets: WidgetListItem[] = [
      ...(firstPageData.widgets ?? []),
      ...remainingWidgets,
    ];

    const groupMap = new Map<string, CatalogWidget[]>();
    for (const w of allWidgets) {
      const groupLabel = w.product_area ?? 'Other';
      if (!groupMap.has(groupLabel)) {
        groupMap.set(groupLabel, []);
      }
      groupMap.get(groupLabel)!.push({
        widget_key: w.widget_key,
        widget_type: w.widget_type,
        name: w.name,
        description: w.description,
        product_area: w.product_area,
      });
    }

    const groups: CatalogGroup[] = [];
    groupMap.forEach((widgets, label) => groups.push({label, widgets}));

    return {groups};
  }, [firstPageData, remainingWidgets]);

  const remainingQueriesRef = React.useRef(remainingQueries);
  remainingQueriesRef.current = remainingQueries;

  const refetch = React.useCallback(() => {
    void refetchFirstPage();
    remainingQueriesRef.current.forEach((q) => void q.refetch());
  }, [refetchFirstPage]);

  return {data, isLoading, isError, refetch};
}
